import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// ============================================================
// Route review — does the day hang together?
// ============================================================
// Superadmin only, by decision. This screen shows derived judgements about how
// somebody spent their day, and derived judgements about people are worth
// restricting until the team has been told the feature exists and what it is
// for.
//
// The wording throughout is chosen to keep the reader honest with themselves.
// GPS from a mid-range phone in a concrete stairwell is wrong often enough that
// every observation here is a question. The screen says so out loud, shows the
// evidence next to the claim, and makes dismissing a flag — with a reason —
// exactly as easy as acting on one.

const SEVERITY = {
  high:   { label: "Worth asking about", bg: "#fef2f2", border: "#fecaca", text: "#b91c1c" },
  review: { label: "Worth a look",       bg: "#fffbeb", border: "#fde68a", text: "#b45309" },
  info:   { label: "Measurement",        bg: "#f8fafc", border: "#e2e8f0", text: "#475569" },
};

const FLAG_TITLES = {
  no_gps:                 "No location data",
  route_gap:              "Tracking went quiet",
  impossible_speed:       "A leg nobody could drive",
  visit_without_stop:     "No stop recorded at this visit",
  visit_without_location: "Visit logged without GPS",
  visit_far_from_stop:    "Nearest stop was some way off",
  visits_same_location:   "Several institutions, one location",
  day_distance_km:        "Distance travelled",
};

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const clock = (v) => v
  ? new Date(String(v).replace(" ", "T")).toLocaleTimeString("en-GB",
      { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true })
  : "";

const mins = (m) => {
  const n = Number(m) || 0;
  return n >= 60 ? `${Math.floor(n / 60)} h ${n % 60} m` : `${n} m`;
};

export default function RouteReview({ accent = "#E31E24" }) {
  const [date, setDate]     = useState(todayIST());
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [note, setNote]     = useState("");

  const loadList = useCallback(async () => {
    setLoad(true);
    try {
      const res = await API.get(`/reports/route-review?date=${date}`);
      setRows(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load route review");
      setRows([]);
    } finally { setLoad(false); }
  }, [date]);

  useEffect(() => { loadList(); setOpenId(null); setDetail(null); }, [loadList]);

  const openRep = async (userId) => {
    if (openId === userId) { setOpenId(null); setDetail(null); return; }
    setOpenId(userId);
    setDetail(null);
    try {
      const res = await API.get(`/reports/route-review/${userId}?date=${date}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load the day");
      setOpenId(null);
    }
  };

  const recompute = async () => {
    setBusy(true);
    try {
      await API.post("/reports/route-review/recompute", { date });
      toast.success("Recomputed from the location log");
      await loadList();
      if (openId) { const id = openId; setOpenId(null); await openRep(id); }
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not recompute");
    } finally { setBusy(false); }
  };

  const dismiss = async (flagId) => {
    if (!note.trim()) return toast.error("Add a short reason — it stays on the record");
    try {
      await API.post(`/reports/route-review/flags/${flagId}/dismiss`, { note: note.trim() });
      toast.success("Dismissed");
      setDismissing(null); setNote("");
      const id = openId; setOpenId(null); await openRep(id);
      await loadList();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not dismiss");
    }
  };

  const card  = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 13, marginBottom: 10 };
  const cell  = { fontSize: 12, color: "#64748b" };
  const value = { fontSize: 17, fontWeight: 800, color: "#0f172a", fontVariantNumeric: "tabular-nums" };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: "0 0 5px", fontSize: 19, fontWeight: 800, color: "#0f172a" }}>Route review</h2>
        <p style={{ margin: 0, fontSize: 12.5, color: "#64748b", lineHeight: 1.65, maxWidth: 640 }}>
          Stops, distance and coherence, worked out from location pings. Pings arrive minutes apart
          and stop when a phone locks, so everything here is a question to ask, not a conclusion to
          act on. Dismiss anything you have already explained — the reason is kept.
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 13, flexWrap: "wrap" }}>
        <input type="date" value={date} max={todayIST()} onChange={e => setDate(e.target.value || todayIST())}
          style={{ padding: "8px 11px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 13, fontFamily: "inherit" }} />
        <button type="button" onClick={recompute} disabled={busy}
          style={{ padding: "8px 14px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff",
                   color: "#475569", fontSize: 12.5, fontWeight: 750, cursor: busy ? "wait" : "pointer", fontFamily: "inherit" }}>
          {busy ? "Recomputing..." : "Recompute this day"}
        </button>
      </div>

      {loading ? (
        <div style={{ ...card, padding: 22, textAlign: "center", color: "#64748b", fontSize: 13 }}>Loading...</div>
      ) : !rows.length ? (
        <div style={{ ...card, padding: 20, color: "#64748b", fontSize: 13, lineHeight: 1.6 }}>
          Nothing recorded for this date — no visits and no location pings.
        </div>
      ) : rows.map(row => (
        <div key={row.user_id} style={card}>
          <button type="button" onClick={() => openRep(row.user_id)}
            style={{ width: "100%", textAlign: "left", background: "none", border: 0, padding: "13px 15px",
                     cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center",
                     gap: 14, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={{ fontSize: 14.5, fontWeight: 750, color: "#0f172a" }}>{row.name}</div>
              <div style={cell}>{row.company === "ebekmed" ? "Ebek Med" : "Ebek"}</div>
            </div>
            <div><div style={value}>{row.visit_count}</div><div style={cell}>visits</div></div>
            <div><div style={value}>{row.route_km != null ? Number(row.route_km).toFixed(1) : "—"}</div><div style={cell}>route km</div></div>
            <div><div style={value}>{row.total_km != null ? Number(row.total_km).toFixed(1) : "—"}</div><div style={cell}>GPS trail km</div></div>
            <div><div style={value}>{row.stop_count}</div><div style={cell}>stops</div></div>
            <div><div style={value}>{mins(row.inside_minutes)}</div><div style={cell}>stopped</div></div>
            <div>
              <div style={{ ...value, color: row.flag_count ? "#b45309" : "#16a34a" }}>{row.flag_count}</div>
              <div style={cell}>to review</div>
            </div>
          </button>

          {openId === row.user_id && (
            <div style={{ borderTop: "1px solid #eef2f7", padding: "13px 15px", background: "#fbfdfe" }}>
              {!detail ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>Loading the day...</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", textTransform: "uppercase",
                                letterSpacing: 0.5, marginBottom: 8 }}>The day as GPS saw it</div>
                  {!detail.stops.length ? (
                    <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
                      No stop of ten minutes or more was detected. That happens when tracking is off,
                      the phone stayed locked, or the day was genuinely all travel.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 6, marginBottom: 15 }}>
                      {detail.stops.map(stop => (
                        <div key={stop.id} style={{ display: "flex", gap: 11, alignItems: "baseline",
                                                    fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                          <span style={{ fontVariantNumeric: "tabular-nums", color: "#64748b", minWidth: 128 }}>
                            {clock(stop.started_at)} – {clock(stop.ended_at)}
                          </span>
                          <span style={{ fontWeight: 700, minWidth: 62 }}>{mins(stop.minutes)}</span>
                          <span style={{ flex: 1 }}>
                            {stop.institution_name
                              ? <>{stop.institution_name}{stop.match_distance_m != null && <span style={{ color: "#94a3b8" }}> · {stop.match_distance_m} m from the logged pin</span>}</>
                              : <span style={{ color: "#94a3b8" }}>No visit logged here</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", textTransform: "uppercase",
                                letterSpacing: 0.5, marginBottom: 8 }}>Observations</div>
                  {!detail.flags.length ? (
                    <div style={{ fontSize: 13, color: "#16a34a" }}>Nothing to raise. The route and the visits agree.</div>
                  ) : (
                    <div style={{ display: "grid", gap: 8 }}>
                      {detail.flags.map(f => {
                        const s = SEVERITY[f.severity] || SEVERITY.info;
                        return (
                          <div key={f.id} style={{ border: `1px solid ${s.border}`, background: f.dismissed ? "#f8fafc" : s.bg,
                                                   borderRadius: 11, padding: "10px 12px", opacity: f.dismissed ? 0.72 : 1 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                              <span style={{ fontSize: 13.5, fontWeight: 750, color: s.text }}>
                                {FLAG_TITLES[f.flag_code] || f.flag_code}
                              </span>
                              <span style={{ fontSize: 10.5, fontWeight: 800, color: s.text, textTransform: "uppercase",
                                             letterSpacing: 0.4 }}>{s.label}</span>
                            </div>
                            <div style={{ fontSize: 12.5, color: "#334155", marginTop: 4, lineHeight: 1.6 }}>{f.detail}</div>

                            {f.dismissed ? (
                              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
                                Dismissed{f.dismissed_by_name ? ` by ${f.dismissed_by_name}` : ""} — “{f.dismiss_note}”
                              </div>
                            ) : f.severity !== "info" && (
                              dismissing === f.id ? (
                                <div style={{ marginTop: 8, display: "flex", gap: 7, flexWrap: "wrap" }}>
                                  <input value={note} onChange={e => setNote(e.target.value)} autoFocus
                                    placeholder="Why is this fine? e.g. phone died at 2pm"
                                    style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 8,
                                             border: "1.5px solid #cbd5e1", fontSize: 12.5, fontFamily: "inherit" }} />
                                  <button type="button" onClick={() => dismiss(f.id)}
                                    style={{ padding: "8px 13px", borderRadius: 8, border: 0, background: accent,
                                             color: "#fff", fontSize: 12, fontWeight: 750, cursor: "pointer", fontFamily: "inherit" }}>
                                    Dismiss
                                  </button>
                                  <button type="button" onClick={() => { setDismissing(null); setNote(""); }}
                                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1",
                                             background: "#fff", color: "#475569", fontSize: 12, fontWeight: 700,
                                             cursor: "pointer", fontFamily: "inherit" }}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button type="button" onClick={() => { setDismissing(f.id); setNote(""); }}
                                  style={{ marginTop: 7, padding: "6px 11px", borderRadius: 8, border: "1px solid #cbd5e1",
                                           background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 750,
                                           cursor: "pointer", fontFamily: "inherit" }}>
                                  Dismiss with a reason
                                </button>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
